<script setup>
import { ref, watch } from "vue";
import { Head, useForm, Link, router, usePage } from "@inertiajs/vue3";
import VHeaderBreadcrumb from "@/Shared/VHeaderBreadcrumb.vue";
import VAlert from "@/Shared/VAlert.vue";
import VueMultiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
import Swal from "sweetalert2";
import Datatables from "@/Shared/Tables/Datatables.vue";
import DatatableFooterWrapper from "@/Shared/Tables/DatatableFooterWrapper.vue";
import axios from "axios";

const page = usePage();

watch(() => page.props.flash.user_credentials, (credentials) => {
    if (credentials && credentials.email) {
        Swal.fire({
            title: 'Account Auto-Generated!',
            html: `
                <div class="text-start mt-3">
                    <p class="text-muted">A new Partner / Client Admin account was successfully created for this template.</p>
                    <div class="bg-light p-3 rounded border">
                        <div class="mb-2"><strong>Email:</strong><br/> <span class="user-select-all">${credentials.email}</span></div>
                        <div><strong>Password:</strong><br/> <span class="font-monospace fw-bold user-select-all fs-5 text-primary">${credentials.password}</span></div>
                    </div>
                    <div class="alert alert-warning py-2 mt-3 mb-0 small">
                        <i class="fas fa-exclamation-triangle me-1"></i> Please copy and save this password securely now. You will not be able to see it again!
                    </div>
                </div>
            `,
            icon: 'success',
            confirmButtonText: 'I have copied the password',
            confirmButtonColor: '#8a2529',
            allowOutsideClick: false,
            allowEscapeKey: false,
        });
    }
}, { immediate: true });

const deleteTemplate = (id) => {
    if (confirm('Are you sure you want to delete this template?')) {
        router.delete(props.additional.urlDelete.replace('__ID__', id), {
            preserveScroll: true
        });
    }
};

let props = defineProps({
    title: String,
    additional: Object,
});

const fileInput = ref(null);
const selectedTemplate = ref(null);
const isExisting = ref(false);
const documentTypeOptions = ref(['LOA', 'SPK', 'PKS']);

const addCustomDocumentType = (newTag) => {
    documentTypeOptions.value.push(newTag);
    form.document_type = newTag;
};

const form = useForm({
    id: "",
    document_type: "",
    name: "",
    event_id: "",
    description: "",
    price: 15000,
    email_name: "",
    template_file: null,
    create_user: true,
    client_email: "",
    client_password: "",
});

const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
    let password = "";
    for (let i = 0; i < 16; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
};

const breadcrumbs = [
    {
        url: "#",
        label: "Inject Template",
    },
];

const handleFileChange = (e) => {
    form.template_file = e.target.files[0];
};

const onSelectTemplate = (selectedOption) => {
    if (selectedOption && selectedOption.id) {
        isExisting.value = true;
        form.id = selectedOption.id;
        form.document_type = selectedOption.config?.document_type || "";
        form.name = selectedOption.name;
        form.event_id = selectedOption.event_id || 0;
        form.description = selectedOption.description || "";
        form.price = selectedOption.price !== null && selectedOption.price !== undefined ? selectedOption.price : 15000;
        form.email_name = selectedOption.email_name || "";
        form.create_user = false; // Disable user creation logic for existing templates
        form.client_email = "";
        form.client_password = "";
    } else {
        onClearTemplate();
    }
};

const addCustomTemplate = (newTagName) => {
    const tag = {
        name: newTagName,
        id: null,
    };
    selectedTemplate.value = tag;
    isExisting.value = false;
    form.id = "";
    form.name = newTagName;
    form.event_id = "";
    form.description = "";
    form.price = 15000;
    form.email_name = "";
    form.create_user = true;
    form.client_email = newTagName.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '@meteraiku.co.id';
    form.client_password = generatePassword();
};

const onClearTemplate = () => {
    selectedTemplate.value = null;
    isExisting.value = false;
    form.id = "";
    form.document_type = "";
    form.name = "";
    form.event_id = "";
    form.description = "";
    form.price = 15000;
    form.email_name = "";
    form.create_user = true;
    form.client_email = "";
    form.client_password = "";
};

watch(selectedTemplate, (newVal) => {
    if (!newVal) {
        onClearTemplate();
    }
});

watch(() => form.name, (newVal) => {
    if (!isExisting.value && form.create_user && newVal) {
        form.client_email = newVal.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '@meteraiku.co.id';
    }
});

watch(() => form.create_user, (newVal) => {
    if (newVal && !isExisting.value) {
        if (!form.client_password) form.client_password = generatePassword();
        if (!form.client_email && form.name) form.client_email = form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '@meteraiku.co.id';
    }
});

const submit = () => {
    form.post(props.additional.urlStore, {
        onSuccess: () => {
            form.reset();
            selectedTemplate.value = null;
            isExisting.value = false;
            if (fileInput.value) {
                fileInput.value.value = "";
            }
        },
    });
};

const getTemplates = (params) => {
    router.get(route('inject-template.index'), params, {
        preserveState: true,
        replace: true,
    });
};

const changePageLength = (value) => {
    getTemplates({ per_page: value });
};

const onFilter = (value) => {
    getTemplates({
        search_fields: value.search_fields,
        search_values: value.search_values,
        per_page: props.additional.filters?.per_page ?? 20,
        order_by: value.order_by,
        order_type: value.order_type,
    });
};

const actionClick = async (value) => {
    if (value.type === "delete") {
        const result = await Swal.fire({
            icon: "warning",
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            showCancelButton: true,
            confirmButtonColor: "#8a2529",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            router.delete(value.url, { preserveScroll: true });
        }
    } else if (value.type === "configure") {
        router.get(value.url);
    }
};

const formatPrice = (price) => {
    if (price === null || price === undefined) return "-";
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(price);
};
</script>

<template>
    <Head>
        <title>{{ title }}</title>
    </Head>

    <div class="p-4 page-bg">
        <VHeaderBreadcrumb :breadcrumbs="breadcrumbs" />

        <VAlert class="mb-4" />

        <!-- Template Creation Card -->
        <div class="card mb-4 border-0 custom-card">
            <div class="card-body p-4 bg-white">
                <form @submit.prevent="submit" enctype="multipart/form-data">
                    
                    <!-- Document Type (Taggable Multiselect) -->
                    <div class="row mb-2 align-items-center">
                        <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">Document Type</label>
                        <div class="col-sm-9">
                            <VueMultiselect
                                v-model="form.document_type"
                                placeholder="Select or type to add new (e.g. LOA, SPK)..."
                                :options="documentTypeOptions"
                                :taggable="true"
                                :multiple="false"
                                open-direction="bottom"
                                @tag="addCustomDocumentType"
                                :disabled="isExisting"
                            />
                            <div v-if="form.errors.document_type" class="text-danger mt-1 small">{{ form.errors.document_type }}</div>
                        </div>
                    </div>

                    <!-- Template Name (Dynamic Taggable Multiselect) -->
                    <div class="row mb-2 align-items-center">
                        <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">Template Name <span class="text-danger">*</span></label>
                        <div class="col-sm-9">
                            <VueMultiselect
                                v-model="selectedTemplate"
                                label="name"
                                track-by="id"
                                placeholder="Type to search/create new template, or select existing..."
                                :options="additional.allTemplates"
                                :taggable="true"
                                open-direction="bottom"
                                @tag="addCustomTemplate"
                                @select="onSelectTemplate"
                                @remove="onClearTemplate"
                            />
                            <input type="hidden" v-model="form.name" required />
                            <div v-if="form.errors.name" class="text-danger mt-1 small">{{ form.errors.name }}</div>
                            <div class="form-text text-muted mt-2">
                                <span v-if="isExisting" class="text-brand fw-bold">
                                    <i class="fas fa-info-circle me-1"></i> Existing template selected. Fields below are locked to match the database values.
                                </span>
                                <span v-else>
                                    <i class="fas fa-info-circle me-1"></i> Type a custom template name and press **Enter** to initialize a new event.
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Event ID -->
                    <div class="row mb-2 align-items-center">
                        <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">Event ID <span class="text-danger">*</span></label>
                        <div class="col-sm-9">
                            <input
                                v-model="form.event_id"
                                type="number"
                                class="form-control custom-select-glow"
                                :class="{ 'bg-light-gray-disabled text-muted': isExisting }"
                                placeholder="e.g. 65 (Event ID from dash.meteraiku.co.id)"
                                :readonly="isExisting"
                                required
                            />
                            <div v-if="form.errors.event_id" class="text-danger mt-1 small">{{ form.errors.event_id }}</div>
                        </div>
                    </div>

                    <!-- Description -->
                    <div class="row mb-2 align-items-center">
                        <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">Description</label>
                        <div class="col-sm-9">
                            <input
                                v-model="form.description"
                                type="text"
                                class="form-control custom-select-glow"
                                :class="{ 'bg-light-gray-disabled text-muted': isExisting }"
                                placeholder="Brief description of this template"
                                :readonly="isExisting"
                            />
                            <div v-if="form.errors.description" class="text-danger mt-1 small">{{ form.errors.description }}</div>
                        </div>
                    </div>

                    <!-- Price -->
                    <div class="row mb-2 align-items-center">
                        <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">Stamp/Ticket Price (IDR)</label>
                        <div class="col-sm-9">
                            <input
                                v-model="form.price"
                                type="number"
                                class="form-control custom-select-glow"
                                :class="{ 'bg-light-gray-disabled text-muted': isExisting }"
                                placeholder="Default: 15000"
                                min="0"
                                :readonly="isExisting"
                            />
                            <div v-if="form.errors.price" class="text-danger mt-1 small">{{ form.errors.price }}</div>
                        </div>
                    </div>

                    <!-- Email Name -->
                    <div class="row mb-2 align-items-center">
                        <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">Sender Name in Email</label>
                        <div class="col-sm-9">
                            <input
                                v-model="form.email_name"
                                type="text"
                                class="form-control custom-select-glow"
                                :class="{ 'bg-light-gray-disabled text-muted': isExisting }"
                                placeholder="Defaults to: Template Name x Meteraiku"
                                :readonly="isExisting"
                            />
                            <div v-if="form.errors.email_name" class="text-danger mt-1 small">{{ form.errors.email_name }}</div>
                        </div>
                    </div>

                    <!-- PDF File Upload (Always Editable for Upload/Overwrite) -->
                    <div class="row mb-2 align-items-center">
                        <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">
                            Template PDF <span v-if="!isExisting" class="text-danger">*</span>
                        </label>
                        <div class="col-sm-9">
                            <input
                                ref="fileInput"
                                type="file"
                                accept=".pdf"
                                class="form-control custom-select-glow"
                                @change="handleFileChange"
                                :required="!isExisting"
                            />
                            <div v-if="form.errors.template_file" class="text-danger mt-1 small">{{ form.errors.template_file }}</div>
                            <div class="form-text text-muted mt-2">
                                <i class="fas fa-info-circle me-1 text-brand"></i> Upload the PDF file of the document template (Max 10MB). <span v-if="isExisting" class="text-success fw-bold">Leave empty to keep existing PDF.</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="!isExisting">
                        <hr class="my-4 border-light-gray" />

                        <!-- User Account Toggle -->
                        <div class="row mb-3 align-items-center">
                            <div class="col-sm-9 offset-sm-3">
                                <div class="form-check form-switch custom-switch">
                                    <input
                                        v-model="form.create_user"
                                        class="form-check-input custom-checkbox"
                                        type="checkbox"
                                        id="createUserToggle"
                                    />
                                    <label class="form-check-label fw-bold label-brand ms-2 cursor-pointer" for="createUserToggle">
                                        Create Partner / Client Admin Account
                                    </label>
                                </div>
                                <div class="form-text text-muted">
                                    If enabled, this template will be associated with a Client Admin account. If the email already exists, it will assign ownership to the existing account.
                                </div>
                            </div>
                        </div>

                        <!-- Client Credentials Fields -->
                        <div v-if="form.create_user" class="row mb-2 align-items-center fade-in">
                            <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">Client Email <span class="text-danger">*</span></label>
                            <div class="col-sm-9">
                                <input
                                    v-model="form.client_email"
                                    type="email"
                                    class="form-control custom-select-glow"
                                    placeholder="Auto-generated from template name..."
                                    required
                                />
                                <div v-if="form.errors.client_email" class="text-danger mt-1 small">{{ form.errors.client_email }}</div>
                            </div>
                        </div>

                        <div v-if="form.create_user" class="row mb-2 align-items-center fade-in">
                            <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">Password <span class="text-danger">*</span></label>
                            <div class="col-sm-9">
                                <div class="input-group">
                                    <input
                                        v-model="form.client_password"
                                        type="text"
                                        class="form-control custom-select-glow font-monospace"
                                        placeholder="Auto-generated password..."
                                        required
                                    />
                                    <button class="btn btn-outline-secondary border-1 border-opacity-25" style="border-color: rgba(0,0,0,0.1) !important;" type="button" @click="form.client_password = generatePassword()" title="Generate New Password">
                                        <i class="fas fa-sync-alt"></i>
                                    </button>
                                </div>
                                <div class="form-text text-muted mt-1">
                                    <i class="fas fa-info-circle me-1"></i> You can change this generated password if needed before submitting.
                                </div>
                                <div v-if="form.errors.client_password" class="text-danger mt-1 small">{{ form.errors.client_password }}</div>
                            </div>
                        </div>


                    </div>

                    <div class="row mt-3">
                        <div class="col-sm-9 offset-sm-3">
                            <button
                                type="submit"
                                class="btn btn-premium px-4 py-2 border-0 shadow-sm"
                                :disabled="form.processing"
                            >
                                <span v-if="form.processing" class="spinner-border spinner-border-sm me-2"></span>
                                <i v-else class="fas fa-magic me-2"></i> {{ isExisting ? 'Update Template' : 'Inject Template' }}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>

        <!-- Recent Templates Table Card -->
        <div class="card border-0 custom-card">
            <div class="card-header bg-transparent border-0 py-4 px-4">
                <div class="d-flex align-items-center">
                    <div class="section-icon-wrapper me-3">
                        <i class="fas fa-list text-brand"></i>
                    </div>
                    <div>
                        <h5 class="fw-bold mb-1 text-dark-gray text-brand-accent">Injected Templates</h5>
                        <p class="text-muted small mb-0">View all templates loaded in the database.</p>
                    </div>
                </div>
            </div>
            
            <div class="card-body p-4 pt-0 mt-3">
                <div class="dataTables_wrapper dt-bootstrap5">
                    <Datatables
                        :columns="additional.columns"
                        :pagination="additional.templates"
                        :filters="additional.filters"
                        @onFilter="onFilter"
                        @onActionClick="actionClick"
                    />
                    <DatatableFooterWrapper
                        :pagination="additional.templates.meta"
                        :filters="additional.filters"
                        @onChange="changePageLength"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Page Layout */
.page-bg {
    background-color: #f7f9fc;
    min-height: 100vh;
}

/* Premium Card Layout */
.custom-card {
    border-radius: 16px !important;
    background: #ffffff;
    box-shadow: 0 10px 30px rgba(138, 37, 41, 0.03), 0 1px 3px rgba(0, 0, 0, 0.01) !important;
    border: 1px solid rgba(138, 37, 41, 0.06) !important;
    overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.custom-card:hover {
    box-shadow: 0 15px 35px rgba(138, 37, 41, 0.05), 0 1px 4px rgba(0, 0, 0, 0.01) !important;
}

/* Premium Section Icon */
.section-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background-color: rgba(138, 37, 41, 0.08);
}

.section-icon-wrapper i {
    font-size: 1.35rem;
}

.text-brand {
    color: #8a2529 !important;
}

.text-brand-accent {
    color: #2b2b2b;
    letter-spacing: -0.02em;
}

/* Form Styles */
.label-brand {
    color: #495057;
    font-size: 0.9rem;
}

.custom-select-glow {
    border-radius: 8px;
    border: 1.5px solid rgba(0, 0, 0, 0.1);
    padding: 10px 14px;
    transition: all 0.25s ease-in-out;
    font-size: 0.95rem;
    color: #333;
}

.custom-select-glow:focus {
    border-color: #8a2529;
    box-shadow: 0 0 0 4px rgba(138, 37, 41, 0.12);
    outline: none;
    background-color: #fff;
}

/* Disabled styling */
.bg-light-gray-disabled {
    background-color: #f1f3f5 !important;
    border-color: #dee2e6 !important;
    color: #6c757d !important;
    cursor: not-allowed;
}

/* Custom switch & switch logic */
.custom-switch .form-check-input {
    width: 3.2em;
    height: 1.7em;
    cursor: pointer;
}

.custom-switch .form-check-input:checked {
    background-color: #8a2529;
    border-color: #8a2529;
}

.cursor-pointer {
    cursor: pointer;
}

/* Premium Buttons */
.btn-premium {
    background: linear-gradient(135deg, #8a2529 0%, #aa3a3e 100%);
    color: #fff;
    font-weight: 600;
    border-radius: 8px;
    padding: 10px 24px;
    letter-spacing: 0.02em;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    box-shadow: 0 4px 10px rgba(138, 37, 41, 0.2);
}

.btn-premium:hover {
    background: linear-gradient(135deg, #741d20 0%, #8a2529 100%);
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(138, 37, 41, 0.3);
}

.btn-premium:active {
    transform: translateY(0);
}

.btn-premium:disabled {
    background: #dcdcdc;
    color: #8e8e8e;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
}

/* Premium Tables styling */
.premium-table-wrapper table {
    border-collapse: separate;
    border-spacing: 0 8px;
    margin-top: 0 !important;
}

.premium-table-wrapper table thead th {
    background-color: #f8f9fa;
    color: #495057;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
    padding: 16px;
    border-bottom: none;
}

.premium-table-wrapper table tbody tr {
    background-color: #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.015);
    border-radius: 8px;
    transition: all 0.2s ease;
}

.premium-table-wrapper table tbody tr:hover {
    background-color: rgba(138, 37, 41, 0.01) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(138, 37, 41, 0.04);
}

.premium-table-wrapper table tbody td {
    padding: 16px;
    border: none;
    color: #333;
    font-size: 0.9rem;
    vertical-align: middle;
}

/* Transitions */
.fade-in {
    animation: fadeIn 0.4s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (min-width: 576px) {
    .text-end-sm {
        text-align: right;
    }
}
</style>
